import { lookupCard } from "../content/index.js";
import type { CardInstance } from "../domain/types/index.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { CardView } from "./CardView.js";
import { UsedPlayFx } from "./UsedPlayFx.js";
import { playFlashColor, playRarityOf, type PlayRarity } from "./playSlamRarity.js";

type UsedCardsProps = {
  cards: readonly CardInstance[];
  notice?: string | null;
  /** 画面遷移で追加を観測できないカードにも、初回表示で着地演出を再生する。 */
  freshOnMountIds?: readonly string[];
};

const FLASH_MS = 450;

type PlayState = {
  ids: readonly string[];
  rarities: readonly PlayRarity[];
};

const NO_PLAY: PlayState = { ids: [], rarities: [] };

function playStateFor(cards: readonly CardInstance[], ids: readonly string[]): PlayState {
  const freshIds = new Set(ids);
  const freshCards = cards.filter((card) => freshIds.has(card.instanceId));
  if (freshCards.length === 0) return NO_PLAY;
  return {
    ids: freshCards.map((card) => card.instanceId),
    rarities: freshCards.map((card) => {
      const definition = lookupCard(card.cardId);
      return definition ? playRarityOf(definition) : "small";
    }),
  };
}

export function UsedCards({ cards, notice, freshOnMountIds = [] }: UsedCardsProps) {
  const knownCardIds = useRef(new Set(cards.map((card) => card.instanceId)));
  const [play, setPlay] = useState<PlayState>(() => playStateFor(cards, freshOnMountIds));

  useEffect(() => {
    const added = cards.filter((card) => !knownCardIds.current.has(card.instanceId));
    knownCardIds.current = new Set(cards.map((card) => card.instanceId));
    if (added.length > 0) setPlay(playStateFor(cards, added.map((card) => card.instanceId)));
  }, [cards]);

  const freshIdSet = useMemo(() => new Set(play.ids), [play.ids]);
  const flashing = play.ids.length > 0;
  const flashRarity = play.rarities.at(-1) ?? null;

  useEffect(() => {
    if (!flashing) return;
    const timer = window.setTimeout(() => {
      setPlay(NO_PLAY);
    }, FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashing, play.ids]);

  return (
    <section
      className={`used-cards${flashing ? " is-play-flash" : ""}`}
      aria-label="実行済み"
      data-card-dropzone="play"
      style={flashRarity ? { ["--play-flash" as string]: playFlashColor(flashRarity) } : undefined}
    >
      <div className="used-head">
        <span>実行済み</span>
        {cards.length > 0 && <b>{cards.length}</b>}
      </div>
      {cards.length > 0 ? (
        <div className="used-row">
          {cards.map((card) => {
            const def = lookupCard(card.cardId);
            return def ? (
              <CardView
                key={card.instanceId}
                card={card}
                definition={def}
                disabled
                className={`used-card${freshIdSet.has(card.instanceId) ? " is-fresh" : ""}`}
              />
            ) : null;
          })}
        </div>
      ) : null}
      <UsedPlayFx rarities={play.rarities} />
      {notice ? (
        <p className="used-notice" role="status">
          <b>現場判断の結果</b>　{notice}
        </p>
      ) : null}
    </section>
  );
}
